import { Link } from "react-router-dom";
import { useCity } from "@/contexts/CityContext";
import { CITIES } from "@/lib/city";

/**
 * Footer — quiet, editorial. Two-column desktop, stacked mobile. Includes:
 *   - Brand blurb + Amida Health attribution.
 *   - Navigation + legal links.
 *   - City switcher: links to other city subdomains (external) for SEO.
 *   - Compliance mast.
 */
export function Footer() {
  const { city } = useCity();

  function cityHref(slug: string): string {
    if (typeof window === "undefined") return `https://${slug}.pulseboard.com.au`;
    const { protocol, hostname } = window.location;
    // In local dev (localhost / *.local) stay on same origin with path for DX
    if (!hostname.includes("pulseboard.com.au")) return `/${slug}`;
    return `${protocol}//${slug}.pulseboard.com.au`;
  }

  return (
    <footer className="border-t border-pb-border/60 bg-pb-surface-2/40 mt-24">
      <div className="mx-auto max-w-6xl px-4 py-12 md:px-8">
        <div className="grid gap-10 md:grid-cols-[1.2fr_1fr_1fr_1fr]">
          {/* Brand + blurb */}
          <div>
            <Link to="/" className="flex items-center gap-3">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-pb-primary" />
              <span className="font-display text-lg font-semibold text-pb-ink">
                PulseBoard
              </span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-pb-ink-3">
              A referral directory for Australian GPs. A product of{" "}
              <a
                href="https://amidahealthai.com.au"
                className="pb-link"
                target="_blank"
                rel="noreferrer"
              >
                Amida Health Pty Ltd
              </a>
              .
            </p>
          </div>

          {/* Navigate */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-pb-ink-2">
              {city.name}
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to="/directory" className="text-pb-ink-2 hover:text-pb-primary">
                  Directory
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-pb-ink-2 hover:text-pb-primary">
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/brief" className="text-pb-ink-2 hover:text-pb-primary">
                  Referral brief
                </Link>
              </li>
              <li>
                <Link to="/auth" className="text-pb-ink-2 hover:text-pb-primary">
                  Sign in
                </Link>
              </li>
            </ul>
          </div>

          {/* Other cities — external subdomain links for SEO */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-pb-ink-2">
              Cities
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              {CITIES.map((c) =>
                c.slug === city.slug ? (
                  <li key={c.slug}>
                    <span
                      className="text-pb-ink-2"
                      aria-current="page"
                    >
                      {c.name}
                    </span>
                  </li>
                ) : (
                  <li key={c.slug}>
                    <a
                      href={cityHref(c.slug)}
                      className="text-pb-ink-2 hover:text-pb-primary"
                    >
                      {c.name}
                      {!c.isActive && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-wider text-pb-ink-3">
                          soon
                        </span>
                      )}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-pb-ink-2">
              Legal
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to="/privacy" className="text-pb-ink-2 hover:text-pb-primary">
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-pb-ink-2 hover:text-pb-primary">
                  Terms of service
                </Link>
              </li>
              <li>
                <a href="mailto:support@pulseboard.com.au" className="text-pb-ink-2 hover:text-pb-primary">
                  Support
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Compliance mast */}
        <div className="mt-12 flex flex-col gap-2 border-t border-pb-border/60 pt-6 text-xs text-pb-ink-3 md:flex-row md:items-center md:justify-between">
          <p>
            &copy; {new Date().getFullYear()} Amida Health Pty Ltd. Data hosted in Australia
            (GCP australia-southeast1).
          </p>
          <p>
            PulseBoard is a practitioner directory. It does not endorse or verify
            clinical outcomes. Listings carry their source and check date.
          </p>
        </div>
      </div>
    </footer>
  );
}
